import { z } from 'zod'

import { IdSchema, LocalizedSchema } from './common'

// Structured sections shared by product categories, products and industries.
// Mirrors the old site's packaging-page model so every section can be laid out
// exactly as the reference, and reordered per page from the CMS.

/** Page sections in render order. `sectionOrder` on each page is a permutation of these. */
export const PACKAGING_SECTIONS = [
  'capabilityStrip',
  'overview',
  'children',
  'structures',
  'gallery',
  'swatches',
  'materialsFinishes',
  'specs',
  'cost',
  'design',
  'applications',
  'process',
  'why',
  'faq',
  'related',
  'cta',
] as const
export const PackagingSectionSchema = z.enum(PACKAGING_SECTIONS)
export type PackagingSection = z.infer<typeof PackagingSectionSchema>

/** Material/finish swatch groups shown in the `swatches` section. */
export const SPEC_GROUPS = ['rigid', 'label', 'carton', 'corrugated', 'bag', 'nonwoven', 'tube', 'component'] as const
export const SpecGroupSchema = z.enum(SPEC_GROUPS)
export type SpecGroup = z.infer<typeof SpecGroupSchema>

export const NamedItemSchema = z.object({ name: LocalizedSchema, description: LocalizedSchema })
export type NamedItem = z.infer<typeof NamedItemSchema>

export const SpecRowSchema = z.object({ label: LocalizedSchema, value: LocalizedSchema })
export type SpecRow = z.infer<typeof SpecRowSchema>

export const PackagingDetailsSchema = z.object({
  /** Page H1 — longer than the short title used in menus, cards and breadcrumbs. */
  heading: LocalizedSchema,
  subtitle: LocalizedSchema,
  badges: z.array(LocalizedSchema).max(12),
  overview: z.array(LocalizedSchema).max(8),
  structuresHeading: LocalizedSchema,
  structures: z.array(NamedItemSchema).max(24),
  materialsHeading: LocalizedSchema,
  materials: z.array(NamedItemSchema).max(24),
  finishesHeading: LocalizedSchema,
  finishes: z.array(NamedItemSchema).max(24),
  customisation: z.array(LocalizedSchema).max(24),
  specs: z.array(SpecRowSchema).max(40),
  applications: z.array(NamedItemSchema).max(24),
  process: z.array(NamedItemSchema).max(16),
  trustPoints: z.array(LocalizedSchema).max(12),
  costNote: LocalizedSchema,
  specGroups: z.array(SpecGroupSchema).max(SPEC_GROUPS.length),
  sectionOrder: z.array(PackagingSectionSchema).max(PACKAGING_SECTIONS.length),
  relatedArticleIds: z.array(IdSchema).max(12),
  /** Related catalogue pages as site paths (`/products/labels`, `/products/labels/wine-labels`); titles resolve live. */
  relatedPaths: z.array(z.string().regex(/^\/products\/[a-z0-9-]+(\/[a-z0-9-]+)?$/, 'A /products/… path')).max(12).default([]),
})
export type PackagingDetails = z.infer<typeof PackagingDetailsSchema>

export const emptyPackagingDetails = (): PackagingDetails => ({
  heading: {},
  subtitle: {},
  badges: [],
  overview: [],
  structuresHeading: {},
  structures: [],
  materialsHeading: {},
  materials: [],
  finishesHeading: {},
  finishes: [],
  customisation: [],
  specs: [],
  applications: [],
  process: [],
  trustPoints: [],
  costNote: {},
  specGroups: [],
  sectionOrder: [...PACKAGING_SECTIONS],
  relatedArticleIds: [],
  relatedPaths: [],
})

// ── Public (locale-resolved) ─────────────────────────────────────────────
const PublicNamedItemSchema = z.object({ name: z.string(), description: z.string() })

export const PublicPackagingDetailsSchema = z.object({
  heading: z.string(),
  subtitle: z.string(),
  badges: z.array(z.string()),
  overview: z.array(z.string()),
  structuresHeading: z.string(),
  structures: z.array(PublicNamedItemSchema),
  materialsHeading: z.string(),
  materials: z.array(PublicNamedItemSchema),
  finishesHeading: z.string(),
  finishes: z.array(PublicNamedItemSchema),
  customisation: z.array(z.string()),
  specs: z.array(z.object({ label: z.string(), value: z.string() })),
  applications: z.array(PublicNamedItemSchema),
  process: z.array(PublicNamedItemSchema),
  trustPoints: z.array(z.string()),
  costNote: z.string(),
  specGroups: z.array(SpecGroupSchema),
  sectionOrder: z.array(PackagingSectionSchema),
})
export type PublicPackagingDetails = z.infer<typeof PublicPackagingDetailsSchema>
